// src/pages/admin/EditAdminService.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import uploadToCloudinary from "../../utils/cloudinary";
import toast from "react-hot-toast";
import { FaFileImage, FaSpinner } from "react-icons/fa";
import bottom_right_icon from "../../Assets/Images/home/domains/bottom_right_icon.png";
import { X, Save, RotateCcw } from "lucide-react";
import { ServiceFormSkeleton } from "./CreateAdminService";

const EditAdminService = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [originalData, setOriginalData] = useState({});

  // placeholders for files actively uploading (preview + isUploading + id)
  const [uploadPlaceholders, setUploadPlaceholders] = useState([]);

  // single icon upload placeholder state
  const [iconPlaceholder, setIconPlaceholder] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);

  const [formData, setFormData] = useState({
    serviceName: "",
    description: [],
    subtitle: "",
    sdgNumber: "",
    sequenceNo: "",
    images: [], // array of cloudinary URLs
    cardBgColor: "#ffffff",
    cardIcon: "", // cloudinary url
  });

  // refs to trigger hidden inputs
  const imagesInputRef = useRef(null);
  const iconInputRef = useRef(null);

  useEffect(() => {
    const fetchService = async () => {
      try {
        if (!id) {
          toast.error("No service ID provided");
          navigate("/admin-programmes");
          return;
        }
        const resp = await axiosInstance.get(`/services/service-by-id/${id}`);
        const data = resp.data || {};
        const serviceData = {
          serviceName: data.serviceName || "",
          sdgNumber: data.sdgNumber || "",
          sequenceNo: data.sequenceNo || "",
          description: data.description || [],
          subtitle: data.subtitle || "",
          images: data.images || [],
          cardBgColor: data.cardBgColor || "#ffffff",
          cardIcon: data.cardIcon || "",
        };
        setFormData(serviceData);
        setOriginalData(serviceData); // Store original data for reset
      } catch (err) {
        console.error("Failed to fetch service:", err);
        toast.error("Failed to load service details");
        navigate("/admin-programmes");
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id, navigate]);

  // --- Description handlers ---
  const handleDescriptionChange = (index, field, value) => {
    const updated = [...formData.description];
    updated[index] = { ...updated[index], [field]: value };
    setFormData((prev) => ({ ...prev, description: updated }));
  };

  const handleAddDescription = () => {
    setFormData((prev) => ({
      ...prev,
      description: [...prev.description, { heading: "", content: "" }],
    }));
  };

  const handleRemoveDescription = (index) => {
    setFormData((prev) => {
      const updated = [...prev.description];
      updated.splice(index, 1);
      return { ...prev, description: updated };
    });
  };

  // --- Images upload (multiple) ---
  const triggerImageInput = (type = "service") => {
    if (type === "service") imagesInputRef.current?.click();
    else if (type === "icon") iconInputRef.current?.click();
  };

  const handleServiceImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // create placeholders with unique ids
    const placeholders = files.map((file, idx) => {
      const id = `${Date.now()}_${idx}`;
      return {
        id,
        file,
        preview: URL.createObjectURL(file),
        isUploading: true,
      };
    });

    setUploadPlaceholders((prev) => [...prev, ...placeholders]);

    // upload each file, when uploaded remove placeholder and add url to formData.images
    files.forEach(async (file, idx) => {
      const placeholderId = placeholders[idx].id;
      try {
        const url = await uploadToCloudinary(file);
        if (!url) throw new Error("Upload failed");

        // remove placeholder
        setUploadPlaceholders((prev) =>
          prev.filter((ph) => ph.id !== placeholderId)
        );

        // add uploaded url to formData.images
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, url],
        }));
        toast.success(`Image uploaded successfully`);
      } catch (err) {
        // remove placeholder and show error
        setUploadPlaceholders((prev) =>
          prev.filter((ph) => ph.id !== placeholderId)
        );
        toast.error(`Failed to upload image: ${file.name}`);
      }
    });

    // clear native input to allow re-upload of same file later
    e.target.value = null;
  };

  const removeServiceImage = (index) => {
    setFormData((prev) => {
      const arr = [...prev.images];
      arr.splice(index, 1);
      return { ...prev, images: arr };
    });
  };

  // --- Icon upload (single) ---
  const handleIconChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // show local preview placeholder immediately
    const id = `icon_${Date.now()}`;
    setIconPlaceholder({ id, file, preview: URL.createObjectURL(file) });
    setIconUploading(true);

    try {
      const url = await uploadToCloudinary(file);
      setIconUploading(false);
      setIconPlaceholder(null);

      if (!url) throw new Error("Icon upload failed");

      // replace the old icon (only one)
      setFormData((prev) => ({ ...prev, cardIcon: url }));
      toast.success("Icon uploaded successfully!");
    } catch (err) {
      setIconUploading(false);
      setIconPlaceholder(null);
      toast.error("Failed to upload icon");
    }

    e.target.value = null;
  };

  const removeIcon = () => {
    setFormData((prev) => ({ ...prev, cardIcon: "" }));
  };

  // --- Validation ---
  const validateForm = () => {
    if (!formData.serviceName.trim()) {
      toast.error("Please enter a service name");
      return false;
    }
    if (formData.description.length === 0) {
      toast.error("Please add at least one description section");
      return false;
    }
    if (!String(formData.sdgNumber).trim()) {
      toast.error("Please enter an SDG Number");
      return false;
    }
    if (!String(formData.sequenceNo).trim()) {
      toast.error("Please enter a sequence number");
      return false;
    }
    if (formData.images.length === 0) {
      toast.error("Please upload at least one image");
      return false;
    }
    if (!formData.cardIcon) {
      toast.error("Please upload a card icon");
      return false;
    }
    return true;
  };

  // --- Save handler ---
  const handleSave = async () => {
    if (!validateForm()) return;

    const spinner = toast.loading("Updating service...");
    try {
      await axiosInstance.put(`/services/edit-service/${id}`, formData);
      toast.success("Service updated successfully!", { id: spinner });
      navigate(-1);
    } catch (err) {
      console.error("Update service error:", err);
      toast.error(err.response?.data?.message || "Failed to update service", {
        id: spinner,
      });
    }
  };

  // --- Reset to original data ---
  const resetToOriginal = () => {
    setFormData({ ...originalData });
    setUploadPlaceholders([]);
    setIconPlaceholder(null);
    setIconUploading(false);
    toast.success("Form reset to original values");
  };

  // --- Check if form has changes ---
  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  if (loading) {
    return <ServiceFormSkeleton isEdit={true} />;
  }

  const ProgrammeCard = ({ icon, bgColor, title, subtitle }) => {
    return (
      <div className="flex w-[90%] mx-auto px-4 flex-col">
        <div className="h-16 w-full"></div>
        <div
          className="flex flex-col relative h-full min-h-[10rem] rounded-2xl shadow-md p-6 transition-all ease-in-out hover:scale-[101%] hover:shadow-xl cursor-pointer"
          style={{ backgroundColor: bgColor }}
        >
          {/* Icon */}
          <div className="bg-white overflow-hidden absolute size-[6rem] sm:size-[7rem] md:size-[8rem] flex items-center justify-center top-0 right-[10%] translate-y-[-50%] rounded-full shadow-xl border-t border-black border-opacity-10 drop-shadow-xl transition-transform duration-200 ease-in-out">
            {icon && (
              <img src={icon} alt="icon" className="w-16 sm:w-20 md:w-auto" />
            )}
            {!icon && <span className="text-gray-400 text-sm">No icon</span>}
          </div>
          <img
            src={bottom_right_icon}
            className="absolute bottom-0 right-0 w-32"
            alt="bottom_right_icon"
          />

          {/* Content */}
          <div className="h-10"></div>
          <div className="h-full">
            <h3 className="text-[#414040] text-[1.16rem] md:text-[1.3rem]">
              SDG- {formData?.sdgNumber ? formData?.sdgNumber : 0}
            </h3>
            <div className="flex flex-col min-h-[5rem] md:min-h-[8rem]">
              <h3 className="text-xl md:text-2xl font-semibold md:max-w-[20rem]  text-wrap text-[#08121A]">
                {title || "Service Name"}
              </h3>
              <p className="py-1 text-lg md:text-xl text-gray-700">
                {subtitle || "Subtitle"}
              </p>
            </div>
            <div className="text-sm md:text-base font-medium text-[#C23F00] hover:underline">
              More Details →
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-full min-h-screen bg-gray-50 text-gray-800 my-6 py-4">
      {/* Left: scrollable form with hidden scrollbar */}
      <div className="flex-1 p-6 overflow-y-auto h-screen px-10 scrollbar-hide">
        <style jsx>{`
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        <div className="mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">
                Edit Service
              </h1>
              <p className="text-gray-600 mt-1">
                Update service information and settings
              </p>
              {hasChanges() && (
                <p className="text-amber-600 text-sm mt-1 font-medium">
                  ⚠ You have unsaved changes
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={resetToOriginal}
                disabled={!hasChanges()}
                className="px-4 py-2 rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges()}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
              >
                <Save size={16} />
                Save Changes
              </button>
            </div>
          </div>

          {/* Service name */}
          <div className="mb-6">
            <label className="block font-medium mb-2 text-gray-700">
              Service Name *
            </label>
            <input
              type="text"
              value={formData.serviceName}
              onChange={(e) =>
                setFormData((p) => ({ ...p, serviceName: e.target.value }))
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              placeholder="Enter service name"
            />
          </div>
          {/* subtitle name */}
          <div className="mb-6">
            <label className="block font-medium mb-2 text-gray-700">
              Sub Heading *
            </label>
            <input
              type="text"
              value={formData.subtitle}
              onChange={(e) =>
                setFormData((p) => ({ ...p, subtitle: e.target.value }))
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              placeholder="Enter subtitle"
            />
          </div>

          <div className="flex flex-col w-full md:flex-row gap-6">
            <div className="w-full">
              {/* Color picker */}
              <div className="mb-6">
                <label className="block font-medium mb-2 text-gray-700">
                  Card Background Color
                </label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full border-2 border-gray-300 shadow-sm cursor-pointer hover:scale-105 transition-transform"
                    style={{ backgroundColor: formData.cardBgColor }}
                    onClick={() => {
                      document.getElementById("card-color-hex")?.focus();
                    }}
                    title="Click hex input to edit"
                  />
                  <input
                    type="color"
                    value={formData.cardBgColor}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        cardBgColor: e.target.value,
                      }))
                    }
                    className="w-12 h-12 p-0 border-none cursor-pointer"
                  />
                  <input
                    id="card-color-hex"
                    value={formData.cardBgColor}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        cardBgColor: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md w-36 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="#ffffff"
                  />
                  <p className="text-sm text-gray-500 ml-2">
                    Tip: Use subtle pastels for best results
                  </p>
                </div>
              </div>

              {/* Icon upload */}
              <div className="mb-6">
                <label className="font-medium mb-2 flex flex-col text-gray-700">
                  Card Icon *{" "}
                  <span className="text-xs text-amber-600 font-normal">
                    Upload icon with black stroke and transparent background
                    <br />
                    Recommended: 75×75 px, Max size: 200KB
                  </span>
                </label>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => triggerImageInput("icon")}
                    className="flex flex-col items-center justify-center w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200"
                  >
                    <FaFileImage className="text-2xl text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">
                      {formData.cardIcon ? "Change Icon" : "Upload Icon"}
                    </span>
                  </button>

                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleIconChange}
                    className="hidden"
                  />

                  <div className="flex gap-3 items-center">
                    <div className="relative w-20 h-20 rounded border-2 border-gray-200 bg-white flex items-center justify-center">
                      {iconUploading && (
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10 rounded">
                          <FaSpinner className="animate-spin text-white" />
                        </div>
                      )}

                      {iconPlaceholder ? (
                        <img
                          src={iconPlaceholder.preview}
                          alt="icon-preview"
                          className="object-contain w-full h-full rounded"
                        />
                      ) : formData.cardIcon ? (
                        <>
                          <img
                            src={formData.cardIcon}
                            alt="card-icon"
                            className="object-contain w-full h-full rounded"
                          />
                          <button
                            onClick={removeIcon}
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                            title="Remove icon"
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <div className="text-xs text-gray-400">No icon</div>
                      )}
                    </div>

                    <div className="text-sm text-gray-500">
                      Recommended: <strong>75×75 px</strong> — Large icons may
                      look off
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Preview */}
            <div className="w-full min-h-64 flex-col flex items-center p-4">
              <label className="block font-medium text-gray-700 mb-4">
                Card Preview
              </label>
              <ProgrammeCard
                icon={formData.cardIcon}
                subtitle={formData.subtitle}
                bgColor={formData.cardBgColor}
                title={formData.serviceName}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 w-full">
            {/* SDG number */}
            <div className="mb-6 w-full">
              <label className="block font-medium mb-2 text-gray-700">
                SDG Number
              </label>
              <input
                type="number"
                value={formData.sdgNumber}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, sdgNumber: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                placeholder="Enter service name"
              />
            </div>
            {/* SEQUENCE number */}
            <div className="mb-6 w-full">
              <label className="block font-medium mb-2 text-gray-700">
                Sequence Number
              </label>
              <input
                type="number"
                value={formData.sequenceNo}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, sequenceNo: e.target.value }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                placeholder="Enter service name"
              />
            </div>
          </div>

          {/* Descriptions */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block font-medium text-gray-700">
                Service Descriptions *
              </label>
              <button
                onClick={handleAddDescription}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              >
                + Add Description
              </button>
            </div>

            {formData.description.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <p className="text-gray-500">No descriptions added yet</p>
                <button
                  onClick={handleAddDescription}
                  className="mt-2 text-blue-600 hover:underline"
                >
                  Add your first description
                </button>
              </div>
            )}

            <div className="space-y-4">
              {formData.description.map((desc, index) => (
                <div
                  key={index}
                  className="border border-gray-200 p-4 rounded-lg bg-white relative shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      Description {index + 1}
                    </span>
                    <button
                      onClick={() => handleRemoveDescription(index)}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline transition-colors"
                    >
                      Remove
                    </button>
                  </div>

                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    Heading
                  </label>
                  <input
                    type="text"
                    value={desc.heading}
                    onChange={(e) =>
                      handleDescriptionChange(index, "heading", e.target.value)
                    }
                    className="w-full border border-gray-300 px-3 py-2 rounded-md mb-3 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="Enter heading"
                  />

                  <label className="block text-sm font-medium mb-1 text-gray-700">
                    Content
                  </label>
                  <textarea
                    value={desc.content}
                    onChange={(e) =>
                      handleDescriptionChange(index, "content", e.target.value)
                    }
                    className="w-full border border-gray-300 px-3 py-2 rounded-md h-24 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="Enter content description"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* SERVICE IMAGES */}
          <div className="mb-6">
            <label className="block font-medium mb-2 text-gray-700">
              Service Images *{" "}
              <span className="text-xs text-amber-600 font-normal">
                (Max size: 10MB per image)
              </span>
            </label>

            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() => triggerImageInput("service")}
                className="flex flex-col items-center justify-center w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200"
              >
                <FaFileImage className="text-2xl text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {formData.images.length + uploadPlaceholders.length > 0
                    ? "Add More"
                    : "Upload Images"}
                </span>
              </button>

              <input
                ref={imagesInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleServiceImageChange}
                className="hidden"
              />

              {/* Images container */}
              <div className="flex-1 overflow-x-auto pb-2">
                {formData.images.length === 0 &&
                  uploadPlaceholders.length === 0 && (
                    <div className="flex items-center justify-center h-24 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                      <p className="text-gray-500 text-sm">
                        No images uploaded yet
                      </p>
                    </div>
                  )}

                <div className="flex gap-3">
                  {/* uploaded images */}
                  {formData.images.map((url, idx) => (
                    <div
                      key={`uploaded_${idx}`}
                      className="relative flex-shrink-0 w-32 h-24"
                    >
                      <img
                        src={url}
                        alt={`uploaded-${idx}`}
                        className="w-full h-full object-cover rounded border-2 border-gray-200 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeServiceImage(idx)}
                        className="absolute top-0 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                        title="Remove image"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}

                  {/* uploading placeholders */}
                  {uploadPlaceholders.map((ph) => (
                    <div
                      key={ph.id}
                      className="relative flex-shrink-0 w-32 h-24"
                    >
                      <img
                        src={ph.preview}
                        alt="preview"
                        className="w-full h-full object-cover rounded border-2 border-gray-200"
                      />
                      {ph.isUploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center z-10 rounded">
                          <FaSpinner className="text-white animate-spin text-lg" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          setUploadPlaceholders((prev) =>
                            prev.filter((p) => p.id !== ph.id)
                          )
                        }
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                        title="Cancel upload"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-2 text-xs  max-w-md text-orange-500">
              Upload multiple images (JPEG, PNG). First (1-3) images will be
              used for slider. <br /> Rest of the images will be shown in the
              gallery section at bottom of the page.
            </p>
          </div>

          {/* Footer actions */}
          <div className="sticky bottom-6 mt-6">
            <div className="flex justify-end gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={resetToOriginal}
                disabled={!hasChanges()}
                className="px-6 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RotateCcw size={16} />
                Reset to Original
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges()}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save size={16} />
                Update Service
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAdminService;
